import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Avatar, Button, Dialog, DialogContent, DialogTitle, MenuItem, TextField } from '@mui/material';
import ArrowForward from '@mui/icons-material/ArrowForward';
import Search from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import DirectoryPage from '../components/directories/DirectoryPage';
import DirectoryPagination from '../components/directories/DirectoryPagination';
import DirectorySavedSearches from '../components/directories/DirectoryPreferences';
import TeamDirectorySummary from '../components/directories/TeamDirectorySummary';
import { useAppContext } from '../context/AppContext';
import { useClubes, useEquipos, useTemporadas } from '../hooks/queries';
import { getEquiposByIdClubAndIdTemporada } from '../services/clubesService';
import { getEstadisticasEquipoByIdEquipo } from '../services/equipoEstadisticasService';
import usePageTitle from '../hooks/usePageTitle';

export function TeamDirectoryCard({ team, season }) {
  const { t } = useTranslation('directories');
  const navigate = useNavigate();
  const location = useLocation();
  const { setFilters } = useAppContext();
  const [action, setAction] = useState(null);
  const [summaryContext, setSummaryContext] = useState(null);
  const teamId = team.idEquipo ?? team.codequipo ?? team.id;
  const competition = team.idCompeticion ?? (team.idCompeticiones?.length === 1 ? team.idCompeticiones[0] : null);
  const group = team.idGrupo ?? (team.idGrupos?.length === 1 ? team.idGrupos[0] : null);
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['directory-team-contexts', season, String(teamId), team.nombre],
    queryFn: () => getEstadisticasEquipoByIdEquipo(teamId, season, navigate),
    enabled: Boolean(action && !(competition && group) && team.nombre && teamId != null),
    staleTime: 15 * 60 * 1000,
    retry: false,
  });
  const contexts = (Array.isArray(data) ? data : []).filter((item) =>
    String(item.idEquipo) === String(teamId) && String(item.idTemporada) === String(season) &&
    (!competition || String(item.idCompeticion) === String(competition)) && item.idCompeticion && item.idGrupo);
  const open = (target, context) => {
    if (target === 'summary') {
      setSummaryContext(context);
      setAction(null);
      return;
    }
    const { idCompeticion, idGrupo } = context;
    const state = { directoryFrom: location.pathname + location.search };
    if (target === 'statistics') {
      const id = `${teamId}_${idCompeticion}_${idGrupo}_${season}`;
      navigate(`/estadisticasEquipos?${new URLSearchParams({ id, idTemporada: season })}`, { state });
    } else {
      setFilters((previous) => ({
        ...previous, temporada: season, temporadaNombre: '', competicion: idCompeticion,
        competicionNombre: context.nombreCompeticion || team.categoria || '', grupo: idGrupo,
        grupoNombre: context.nombreGrupo || '', federacion: null, federacionNombre: '', categoria: null,
      }));
      navigate(target === 'matches' ? `/partidos?${new URLSearchParams({ q: team.nombre })}`
        : `/rankingJugadores?${new URLSearchParams({ team: team.nombre, teamId: String(teamId) })}`, { state });
    }
  };
  const choose = (target) => {
    if (target === 'summary' && summaryContext) {
      setSummaryContext(null);
      return;
    }
    if (competition && group) open(target, { idCompeticion: competition, idGrupo: group });
    else setAction(target);
  };
  return <article className="dr-team-card">
    <div><h3>{team.nombre}</h3><p>{team.categoria || team.nombreCompeticion || '—'}</p></div>
    <div className="dr-team-actions">{['statistics', 'matches', 'squad', 'summary'].map((target) =>
      <Button key={target} onClick={() => choose(target)} endIcon={target === 'statistics' ? <ArrowForward /> : undefined}
        aria-expanded={target === 'summary' ? Boolean(summaryContext) : undefined}>
        {t(target === 'summary' && summaryContext ? 'hideSummary' : target)}
      </Button>)}</div>
    {summaryContext && <TeamDirectorySummary teamId={teamId} season={season} context={summaryContext} />}
    <Dialog open={Boolean(action)} onClose={() => setAction(null)} fullWidth maxWidth="sm">
      <DialogTitle>{team.nombre}</DialogTitle>
      <DialogContent>
        <p>{t('context')}</p>
        {isLoading && <p role="status">{t('loading')}</p>}
        {isError && <Alert severity="error" action={<Button onClick={() => refetch()}>{t('retry')}</Button>}>{t('loadError')}</Alert>}
        {!isLoading && !isError && contexts.length === 0 && <p>{t('noContext')}</p>}
        <div className="dr-stack">{contexts.map((context) => <Button key={context.id}
          onClick={() => open(action, context)} sx={{ minHeight: 48, justifyContent: 'flex-start' }}>
          {context.nombreCompeticion || team.categoria || t('competition', { id: context.idCompeticion })}
          {' · '}{context.nombreGrupo || t('group', { id: context.idGrupo })}
        </Button>)}</div>
        <Button onClick={() => setAction(null)} sx={{ mt: 2 }}>{t('back', { ns: 'common', defaultValue: 'Volver' })}</Button>
      </DialogContent>
    </Dialog>
  </article>;
}

export default function ClubesPageModern({ favorite, teamsOnly = false }) {
  const { t } = useTranslation('directories');
  usePageTitle(t(teamsOnly ? 'teams' : 'clubs'));
  const { filters } = useAppContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const query = params.get('q')?.trim() || '';
  const mode = teamsOnly ? 'teams' : 'clubs';
  const category = params.get('category') || '';
  const [draft, setDraft] = useState(query);
  const { data: seasons = [], isError: seasonsError, refetch: retrySeasons } = useTemporadas();
  const sortedSeasons = [...seasons].sort((a, b) => Number(b.idTemporada ?? b.id) - Number(a.idTemporada ?? a.id));
  const season = params.get('season') || String(filters.temporada || sortedSeasons[0]?.idTemporada || sortedSeasons[0]?.id || '');
  const clubId = params.get('club');
  const clubsQuery = useClubes(query, { enabled: mode === 'clubs' && query.length >= 2 });
  const teamsQuery = useEquipos(season, query, { enabled: mode === 'teams' && query.length >= 2 && Boolean(season) });
  const selected = (clubsQuery.data || []).find((club) => String(club.id) === clubId);
  const clubTeamsQuery = useQuery({
    queryKey: ['club-teams', season, clubId],
    queryFn: () => getEquiposByIdClubAndIdTemporada(clubId, season, navigate),
    enabled: Boolean(mode === 'clubs' && clubId && season),
    staleTime: 15 * 60 * 1000,
    retry: false,
  });
  useEffect(() => { setDraft(query); }, [query]);
  const update = (changes) => setParams((previous) => {
    const next = new URLSearchParams(previous);
    if (['q', 'season', 'club', 'category'].some((field) => Object.hasOwn(changes, field))) next.delete('page');
    if (['q', 'season', 'club'].some((field) => Object.hasOwn(changes, field))) next.delete('category');
    Object.entries(changes).forEach(([key, value]) => value ? next.set(key, value) : next.delete(key));
    return next;
  }, { replace: true });
  const resultQuery = mode === 'clubs' ? clubsQuery : teamsQuery;
  const resultItems = resultQuery.data || [];
  const results = (mode === 'clubs' ? resultItems.filter((club, index) =>
    resultItems.findIndex((item) => String(item.id) === String(club.id)) === index) : [...resultItems])
    .sort((a, b) => String(a.nombre).localeCompare(String(b.nombre)));
  const clubTeams = [...(clubTeamsQuery.data || [])].sort((a, b) => Number(a.orden || 0) - Number(b.orden || 0));
  const categories = [...new Set((teamsOnly ? results : clubTeams).map((team) => team.categoria || team.nombreCompeticion).filter(Boolean))].sort();
  const matchesCategory = (team) => !category || (team.categoria || team.nombreCompeticion) === category;
  const filteredTeams = results.filter(matchesCategory);
  const pageSize = 25;
  const page = Math.min(Math.max(1, Math.floor(Number(params.get('page')) || 1)), Math.max(1, Math.ceil(filteredTeams.length / pageSize)));
  const error = (state) => state.isError && <Alert severity="error"
    action={<Button onClick={() => state.refetch()}>{t('retry')}</Button>}>{t('loadError')}</Alert>;
  return <DirectoryPage title={t(teamsOnly ? 'teamsTitle' : 'clubsTitle')} subtitle={t(teamsOnly ? 'teamsSubtitle' : 'clubsSubtitle')}
    actions={favorite && <Button variant="contained" onClick={() => navigate(`/estadisticasEquipos?${new URLSearchParams({
      id: `${favorite.idEquipo}_${favorite.idCompeticion}_${favorite.idGrupo}_${favorite.idTemporada}`,
      idTemporada: favorite.idTemporada,
    })}`, { state: { directoryFrom: location.pathname + location.search } })}>{t('myTeam')}</Button>}>
    <form className="dr-panel dr-toolbar" onSubmit={(event) => { event.preventDefault(); update({ q: draft.trim(), club: null }); }}>
      <TextField className="dr-search" label={t(teamsOnly ? 'searchTeam' : 'searchClub')} placeholder={t('clubSearch')}
        value={draft} onChange={(event) => setDraft(event.target.value)} helperText={t('searchHint')} />
      <TextField select label={t('season')} value={season} onChange={(event) => update({ season: event.target.value })}
        SelectProps={{ native: true }}>
        {season && !sortedSeasons.some((item) => String(item.idTemporada ?? item.id) === season) &&
          <option value={season}>{season}</option>}
        {!season && <option value="">—</option>}
        {sortedSeasons.map((item) => <option key={item.idTemporada ?? item.id} value={item.idTemporada ?? item.id}>{item.nombre}</option>)}
      </TextField>
      <Button type="submit" variant="contained" startIcon={<Search />} disabled={draft.trim().length < 2 || !season}>{t('search')}</Button>
    </form>
    {seasonsError && <Alert severity="error" action={<Button onClick={() => retrySeasons()}>{t('retry')}</Button>}>{t('loadError')}</Alert>}
    <DirectorySavedSearches scope={teamsOnly ? 'teams' : 'clubs'} />
    {(teamsOnly || clubId) && <div className="dr-panel dr-toolbar">
      <TextField select label={t('teamCategory')} value={category} onChange={(event) => update({ category: event.target.value })}>
        <MenuItem value="">{t('allCategories')}</MenuItem>
        {category && !categories.includes(category) && <MenuItem value={category}>{category}</MenuItem>}
        {categories.map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
      </TextField>
      <span className="dr-muted">{t('contextHint')}</span>
    </div>}
    <div className={mode === 'clubs' ? 'dr-layout dr-clubs-layout' : ''}>
      <section className="dr-panel" aria-label={t(mode)}>
        <h2>{t(mode)}</h2>
        {error(resultQuery)}
        {resultQuery.isLoading && query.length >= 2 && <p role="status">{t('loading')}</p>}
        {query.length < 2 && <p className="dr-empty">{t('searchHint')}</p>}
        {!resultQuery.isLoading && query.length >= 2 && !resultQuery.isError && results.length === 0 &&
          <p className="dr-empty">{t('noResults')}</p>}
        {teamsOnly && results.length > 0 && !results.some(matchesCategory) && <p className="dr-empty">{t('noResults')}</p>}
        <div className={mode === 'clubs' ? 'dr-club-list' : 'dr-team-list'}>
          {(teamsOnly ? filteredTeams.slice((page - 1) * pageSize, page * pageSize) : results).map((item) => mode === 'teams' ? <TeamDirectoryCard key={`${season}:${item.id}`} team={item} season={season} />
            : <button key={item.id} className="dr-club-choice" aria-pressed={String(item.id) === clubId}
              onClick={() => update({ club: String(item.id) })}>
              <Avatar src={item.escudo} alt="" className="dr-avatar">{item.nombre?.[0]}</Avatar>
              <span><strong>{item.nombre}</strong><small>{[item.localidad, item.provincia].filter(Boolean).join(' · ')}</small></span>
            </button>)}
        </div>
        {teamsOnly && <DirectoryPagination page={page} pageSize={pageSize} total={filteredTeams.length}
          onChange={(value) => update({ page: String(value) })} />}
      </section>
      {mode === 'clubs' && <div className="dr-stack">
        {clubId ? <>
          <section className="dr-panel dr-identity" aria-label={t('clubInfo')}>
            <Avatar src={selected?.escudo} alt="" className="dr-avatar" sx={{ width: 64, height: 64 }}>{selected?.nombre?.[0]}</Avatar>
            <div><h2>{selected?.nombre || t('clubInfo')}</h2>
              <p>{[selected?.localidad, selected?.provincia].filter(Boolean).join(' · ')}</p></div>
          </section>
          <section className="dr-panel" aria-label={t('clubTeams')}>
            <h2>{t('clubTeams')}</h2>{error(clubTeamsQuery)}
            {clubTeamsQuery.isLoading && <p role="status">{t('loading')}</p>}
            {!clubTeamsQuery.isLoading && !clubTeamsQuery.isError && clubTeams.length === 0 && <p className="dr-empty">{t('noResults')}</p>}
            {clubTeams.length > 0 && !clubTeams.some(matchesCategory) && <p className="dr-empty">{t('noResults')}</p>}
            <div className="dr-team-list">{clubTeams.filter(matchesCategory).map((team) => <TeamDirectoryCard key={`${season}:${team.id}`} team={team} season={season} />)}</div>
          </section>
        </> : <section className="dr-panel"><h2>{t('clubTeams')}</h2><p className="dr-empty">{t('chooseClub')}</p></section>}
      </div>}
    </div>
  </DirectoryPage>;
}
